import { useCallback, useContext, useEffect, useRef } from 'react';
import { MoonPayContext } from 'src/components/MoonPayProvider';
import { Logger } from 'src/logger';
import type {
  MoonPayWebSdk,
  MoonPayWebSdkConfig,
  OnUrlSignatureRequested,
} from 'src/types';

export const useSdk = (
  config: MoonPayWebSdkConfig,
  visible: boolean,
  onUrlSignatureRequested?: OnUrlSignatureRequested,
) => {
  const sdk = useRef<MoonPayWebSdk | null>(null);
  const { init, debug } = useContext(MoonPayContext);
  const logger = useRef(new Logger().if(debug || false));

  const handleUpdateSignatureAndShow = useCallback(async () => {
    if (!sdk.current) {
      return;
    }

    if (
      !onUrlSignatureRequested &&
      ('walletAddress' in config || 'walletAddresses' in config)
    ) {
      logger.current.warn(
        'You must provide an `onUrlSignatureRequested` function to sign the URL when using the `walletAddress` or `walletAddresses` prop',
      );
    }

    if (onUrlSignatureRequested) {
      const url = sdk.current.generateUrlForSigning();

      try {
        const updatedSignature = await onUrlSignatureRequested?.(url);
        if (!updatedSignature) {
          return;
        }

        sdk.current?.updateSignature(updatedSignature);
      } catch (e) {
        logger.current.error(
          'Something went wrong calling the provided `onUrlSignatureRequested` function:',
          e,
        );
      }
    }

    if (visible) {
      sdk.current?.show();
    }
  }, [onUrlSignatureRequested, visible, config]);

  useEffect(() => {
    // If the SDK is already initialized, close it and reinitialize it with new config
    if (sdk.current) {
      sdk.current.close();
    }

    const newSdk = init?.(config) || null;
    sdk.current = newSdk;

    handleUpdateSignatureAndShow();
  }, [init, config, handleUpdateSignatureAndShow]);
};
